import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractComponent } from '../../shared/components/abstract-component/abstract.component';
import { LinkComponent, NavTarget } from '../../shared/components/link/link.component';
import { TableSortModel } from '../../shared/tablesort/tableSortModel';
import { LastRechtRootEntryVm } from '../../core/services/standardrecht-viewmodel.service';
import { Router } from '@angular/router';
import { FormatConfigService } from '../../core/services/format-config.service';
import { AppPermissionService } from '../../core/services/app-permission.service';
import { ComparatorService } from '../../shared/services/comparator-service';
import { CapiLoginService } from '../../core/extension/login/capi-login-service';
import { DokulinkItemBuilderFactory } from '../../shared/components/dokulink/dokulinkItemBuilderFactory';
import { keysToTranslate } from '../../../i18n/keys-to-translate';
import { ListLoader } from '../../shared/components/list-loader/list-loader';
import { ListLoaderWrapperComponent } from '../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { DatumPipe } from '../../shared/pipes/simple/datum.pipe';
import { HistoryDirective } from '../../shared/directives/history.directive';
import { KompaktBelegComponent } from '../../shared/kompaktdarstellung/beleg/kompakt-beleg.component';
import {
  KompaktStandardrechtComponent
} from '../../shared/kompaktdarstellung/standardrecht/kompakt-standardrecht.component';
import { KompaktTimepointComponent } from '../../shared/kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { FormatRechteIdPipe } from '../../shared/pipes/format/format-rechte-id.pipe';
import { MapsComponent } from '../../shared/widgets/maps/maps.component';
import { KompaktGrundstueckComponent } from '../../shared/kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import { OrderByLastRechtPersonEntryVmPipe } from '../../shared/pipes/order/order-by-gb-lastrechtpersonentry.pipe';
import { KompaktPersongbComponent } from '../../shared/kompaktdarstellung/persongb/kompakt-persongb.component';
import {
  KompaktPersonenadresseComponent
} from '../../shared/kompaktdarstellung/personenadresse/kompakt-personenadresse.component';
import { ReadMoreComponent } from '../../shared/widgets/read-more/read-more.component';
import { ErweiterungConfigResponse } from '../../core/base-api';
import { PersonGbListEntryMeta } from '../../core/gb-api';
import { RechtsgrundIcpComponent } from '../shared/component/rechtsgrund-icp/rechtsgrund-icp.component';
import {
  KompaktGrundpfandrechtIcpComponent
} from '../shared/component/kompakt-grundpfandrecht-icp/kompakt-grundpfandrecht-icp.component';
import { KompaktBelegIcpComponent } from '../shared/component/kompakt-beleg-icp/kompakt-beleg-icp.component';
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;
import AuskunftsperreEnum = PersonGbListEntryMeta.AuskunftsperreEnum;

@Component({
  selector: 'cap-standardrecht-table-icp',
  imports: [
    ListLoaderWrapperComponent,
    TranslatePipe,
    NgTemplateOutlet,
    DatumPipe,
    NgClass,
    HistoryDirective,
    KompaktBelegComponent,
    KompaktStandardrechtComponent,
    LinkComponent,
    KompaktTimepointComponent,
    FormatRechteIdPipe,
    MapsComponent,
    KompaktGrundstueckComponent,
    OrderByLastRechtPersonEntryVmPipe,
    KompaktPersongbComponent,
    KompaktPersonenadresseComponent,
    ReadMoreComponent,
    RechtsgrundIcpComponent,
    KompaktGrundpfandrechtIcpComponent,
    KompaktBelegIcpComponent
  ],
  templateUrl: './standardrecht-table-icp.component.html',
  styleUrl: './standardrecht-table-icp.component.scss'
})
export class StandardrechtTableIcpComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input()
  showGrundstuecke: boolean = true;
  @Input()
  showGehoertZu: boolean = true;
  @Input()
  showNachgang: boolean = true;
  @Input()
  showBemerkung: boolean = true;
  @Input()
  showVertragtext: boolean = true;
  @Input()
  hasRight: boolean = true;
  @Input()
  showRechtsgrund: boolean = false;
  @Input()
  rechtArt: RechtArt = RechtArt.DEFAULT;
  @Input()
  isEigentuemer: boolean = false;

  tableSortModel = new TableSortModel<LastRechtRootEntryVm>('table', this.comparatorService);

  enableNavigation: boolean = false;
  grundstueckNavTarget: NavTarget = NavTarget.NAV_GRUNDSTUECK_ES;
  isEigentuemerOrLoggedIn = false;
  hasParameterBelegeAnzeigen = false;
  isGeschaeftsmodulCapWebErweiterung: boolean;
  keineTranslateKeys: string = 'Keine';

  protected readonly AuskunftsperreEnum = AuskunftsperreEnum;
  private changeDetectionTimeout: any;

  constructor(
    protected router: Router,
    protected appformatConfigService: FormatConfigService,
    protected permissionService: AppPermissionService,
    protected comparatorService: ComparatorService,
    protected loginService: CapiLoginService,
    private cdr: ChangeDetectorRef,
    protected dokuLinkBuilderFactory: DokulinkItemBuilderFactory
  ) {
    super();
    keysToTranslate<string>([
      'Belastet',
      'Berechtigt',
      'BelastetBerechtigt',
      'ZuLasten',
      'ZuGunsten',
      'ZuGunstenLasten',
      'Bezeichnung',
      'DatumOderBelegOderRechtsgrund',
      'gehoertZu',
      'KeineBzwGemaessGrundbuch',
      'DatumEntstehung',
      'DominantIndet'
    ]);
  }

  private _listLoader: ListLoader<LastRechtRootEntryVm>;

  get listLoader(): ListLoader<LastRechtRootEntryVm> {
    return this._listLoader;
  }

  @Input()
  set listLoader(listLoader: ListLoader<LastRechtRootEntryVm>) {
    if (!listLoader) {
      return;
    }
    this._listLoader = listLoader;
    this._listLoader.applySortModel(this.tableSortModel);
    this.checkBerechtigungen();
  }

  ngOnInit(): void {
    this.activateChangeDetectionTrigger();
    this.tableSortModel
      .addColumn('EnstehungSort', (a, b) => this.comparatorService.lastRechtRootEntryVmComparator(a, b))
      .switchSortDirection();
    this.addSub(
      this.appformatConfigService.parameterConfig$.subscribe((config) => {
        this.hasParameterBelegeAnzeigen = <any>config?.parameters.BELEGEANZEIGEN;
        this.checkBerechtigungen();
      })
    );
    this.addSub(
      this.appformatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.isGeschaeftsmodulCapWebErweiterung = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.GESCHAEFTSMODUL_CAP_WEB);
      })
    );
    if (!this.isEigentuemer && this.rechtArt === RechtArt.ANMERKUNG) {
      this.keineTranslateKeys = 'KeineBzwGemaessGrundbuch';
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    clearTimeout(this.changeDetectionTimeout);
  }

  private activateChangeDetectionTrigger() {
    this.changeDetectionTimeout = setTimeout(() => {
      this.cdr.markForCheck();
      this.activateChangeDetectionTrigger();
    }, 3000);
  }

  private checkBerechtigungen(): void {
    this.isEigentuemerOrLoggedIn = this.isEigentuemer || this.loginService.userLoggedIn;
  }
}

export enum RechtArt {
  DEFAULT,
  ANMERKUNG,
  VORMERKUNG
}
